/**
 * @file manifest.js
 * @description 静态资源统一注册表。Logo、自设计图标、背景、占位图仅从此文件 export。
 * @module shared/assets
 *
 * 使用方式：
 *   import { BRAND_ASSETS, resolveUiIcon } from '@/shared/assets/manifest'
 *   <AppIcon name="orangeCoin" />
 *   <AppIcon name="like" :variant="liked ? 'active' : 'default'" />
 *
 * 新增资源：放入 src/assets/icons/ui/ → 在此 import 并注册 → 组件经 AppIcon 调用
 * 详见 docs/assets-standard.md
 */

// =============================================================================
// 品牌 Brand
// =============================================================================

import logo from '@/assets/brand/logo.png'

/**
 * Logo、favicon 等品牌资产
 * @type {{ logo: string, logoIcon: string, favicon: string, tipThanks: string }}
 */
export const BRAND_ASSETS = {
  /** 导航栏、页脚、登录/注册弹窗主 Logo */
  logo,
  /** 仅图形标（小尺寸），待提供 logo-icon.svg */
  logoIcon: logo,
  /** 打赏弹窗感谢插图（占位：暂用 logo） */
  tipThanks: logo,
  /** 站点 favicon，同步至 public/favicon.ico */
  favicon: '/favicon.ico',
}

// =============================================================================
// UI 图标（暖墨赤橘套装，英文文件名；经 AppIcon 引用）
// =============================================================================

import home from '@/assets/icons/ui/home.svg'
import compass from '@/assets/icons/ui/compass.svg'
import search from '@/assets/icons/ui/search.svg'
import bell from '@/assets/icons/ui/bell.svg'
import user from '@/assets/icons/ui/user.svg'
import menu from '@/assets/icons/ui/menu.svg'
import chevronRight from '@/assets/icons/ui/chevron-right.svg'
import arrowLeft from '@/assets/icons/ui/arrow-left.svg'
import wallet from '@/assets/icons/ui/wallet.svg'
import orangeCoin from '@/assets/icons/ui/orange-coin.svg'
import totalSpend from '@/assets/icons/ui/total-spend.svg'
import calendarCheck from '@/assets/icons/ui/calendar-check.svg'
import crown from '@/assets/icons/ui/crown.svg'
import profile from '@/assets/icons/ui/profile.svg'
import heart from '@/assets/icons/ui/heart.svg'
import heartFilled from '@/assets/icons/ui/heart-filled.svg'
import like from '@/assets/icons/ui/like.svg'
import likeFilled from '@/assets/icons/ui/like-filled.svg'
import history from '@/assets/icons/ui/history.svg'
import comment from '@/assets/icons/ui/comment.svg'
import feedback from '@/assets/icons/ui/feedback.svg'
import lock from '@/assets/icons/ui/lock.svg'
import unlock from '@/assets/icons/ui/unlock.svg'
import link from '@/assets/icons/ui/link.svg'
import book from '@/assets/icons/ui/book.svg'
import star from '@/assets/icons/ui/star.svg'
import starFilled from '@/assets/icons/ui/star-filled.svg'
import gift from '@/assets/icons/ui/gift.svg'
import camera from '@/assets/icons/ui/camera.svg'
import filter from '@/assets/icons/ui/filter.svg'
import play from '@/assets/icons/ui/play.svg'
import bookmark from '@/assets/icons/ui/bookmark.svg'
import share from '@/assets/icons/ui/share.svg'
import close from '@/assets/icons/ui/close.svg'
import check from '@/assets/icons/ui/check.svg'
import eye from '@/assets/icons/ui/eye.svg'
import tag from '@/assets/icons/ui/tag.svg'
import chevronDown from '@/assets/icons/ui/chevron-down.svg'
import chevronUp from '@/assets/icons/ui/chevron-up.svg'
import refresh from '@/assets/icons/ui/refresh.svg'
import creditCard from '@/assets/icons/ui/credit-card.svg'
import logout from '@/assets/icons/ui/logout.svg'
import doubleArrowRight from '@/assets/icons/ui/double-arrow-right.svg'
import danmakuSend from '@/assets/icons/ui/danmaku-send.svg'
import danmakuSettings from '@/assets/icons/ui/danmaku-settings.svg'
import markAllRead from '@/assets/icons/ui/mark-all-read.svg'
import diamond from '@/assets/icons/ui/diamond.svg'
import toggleOff from '@/assets/icons/ui/toggle-off.svg'
import toggleOn from '@/assets/icons/ui/toggle-on.svg'

/**
 * 单状态 UI 图标（camelCase 键 = AppIcon name）
 * 含语义别名，兼容旧调用名（userCoin / navigationBack 等）
 * @type {Record<string, string>}
 */
export const UI_ICONS = {
  // —— 新套装正式键 ——
  home,
  compass,
  search,
  bell,
  user,
  menu,
  chevronRight,
  arrowLeft,
  wallet,
  orangeCoin,
  totalSpend,
  calendarCheck,
  crown,
  profile,
  history,
  comment,
  feedback,
  lock,
  unlock,
  link,
  book,
  gift,
  camera,
  filter,
  play,
  bookmark,
  share,
  eye,
  tag,
  chevronDown,
  chevronUp,
  refresh,
  close,
  check,
  creditCard,
  logout,
  doubleArrowRight,
  danmakuSend,
  danmakuSettings,
  markAllRead,
  diamond,
  toggleOff,
  toggleOn,

  // —— 语义别名（旧业务键 → 新资产）——
  /** @deprecated 请改用 orangeCoin / wallet */
  userCoin: orangeCoin,
  /** @deprecated 请改用 calendarCheck */
  userCheckin: calendarCheck,
  /** @deprecated 请改用 crown */
  userCrown: crown,
  /** @deprecated 请改用 profile */
  userProfile: profile,
  /** @deprecated 请改用 heart */
  userFavorite: heart,
  /** @deprecated 请改用 history */
  userHistory: history,
  /** @deprecated 请改用 feedback */
  userFeedback: feedback,
  /** @deprecated 请改用 lock */
  userPassword: lock,
  /** @deprecated 请改用 home */
  userSpace: home,
  /** @deprecated 请改用 arrowLeft */
  navigationBack: arrowLeft,
  /** @deprecated 请改用 chevronRight */
  navigationEnter: chevronRight,
  /** @deprecated 请改用 lock */
  volLock: lock,
  /** @deprecated 请改用 unlock */
  volUnlock: unlock,
  /** @deprecated 请改用 book */
  metaBook: book,
  /** @deprecated 请改用 tag */
  metaTag: tag,
  /** @deprecated 请改用 check */
  metaStatus: check,
  /** @deprecated 请改用 danmakuSend */
  danmakuRocket: danmakuSend,
}

/**
 * 多状态图标（default 未选 / active 选中）
 * @type {Record<string, { default: string, active: string }>}
 */
export const UI_ICON_VARIANTS = {
  /** 点赞 */
  like: { default: like, active: likeFilled },
  /** @deprecated 同 like */
  thumbUp: { default: like, active: likeFilled },
  /** 喜欢 / 红心 */
  heart: { default: heart, active: heartFilled },
  /** 星标收藏 */
  star: { default: star, active: starFilled },
  /** 弹幕显示开关 */
  danmakuToggle: { default: toggleOff, active: toggleOn },
}

// =============================================================================
// 背景 Backgrounds
// =============================================================================

import homeHero from '@/assets/backgrounds/home-hero.jpg'

/** @type {{ homeHero: string }} */
export const BACKGROUND_ASSETS = {
  homeHero,
}

// =============================================================================
// 占位 Placeholders
// =============================================================================

/** @type {{ defaultAvatar: string }} */
export const PLACEHOLDER_ASSETS = {
  defaultAvatar: '/img/default-avatar.png',
}

// =============================================================================
// 插图 Illustrations
// =============================================================================

/** @type {Record<string, string>} */
export const ILLUSTRATION_ASSETS = {}

// =============================================================================
// 工具函数
// =============================================================================

/**
 * 解析 UI 图标 URL，支持 variant
 * @param {string} name - UI_ICONS 或 UI_ICON_VARIANTS 的键
 * @param {'default'|'active'} [variant='default']
 * @returns {string}
 */
export function resolveUiIcon(name, variant = 'default') {
  const variants = UI_ICON_VARIANTS[name]
  if (variants) {
    return variants[variant] ?? variants.default ?? ''
  }
  return UI_ICONS[name] ?? ''
}

/**
 * 判断 img src 是否已是默认头像（兼容 webpack 哈希 URL）
 * @param {string} src
 * @returns {boolean}
 */
export function isDefaultAvatarSrc(src) {
  return Boolean(src && String(src).includes('default-avatar'))
}
